import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkButton } from '@/components/ui/buttons/LinkButton';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Card } from '@/components/ui/cards/Card';
import { colors } from '@/assets/styles/colors';
import { zIndex } from '@/assets/styles';
import type { UIExportFormat } from '@/types/fileExport.types';
import { FILE_FORMAT_REGISTRY } from '@/types/fileExport.types';


export interface ExportOption {
  format: UIExportFormat;
  label: string;
  customVariant: {
    default: { backgroundColor: string; textColor: string; borderColor: string };
    hover: { backgroundColor: string; textColor: string; borderColor: string };
    pressed: { backgroundColor: string; textColor: string; borderColor: string };
    focus: { backgroundColor: string; textColor: string; borderColor: string };
  };
}

export interface ExportDropdownProps {
  className?: string;
  cardClassName?: string;
  onExport: (format: UIExportFormat) => void;
  placement?: 'left' | 'right';
  closeOnOutsideClick?: boolean;
  closeOnSelect?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  availableFormats?: UIExportFormat[];
  disabledFormats?: UIExportFormat[];
  disabledFormatMessageKey?: string;
}

const DEFAULT_EXPORT_OPTIONS: ExportOption[] = [
  {
    format: 'EXCEL',
    label: FILE_FORMAT_REGISTRY.EXCEL.label,
    customVariant: {
      default: {
        backgroundColor: 'transparent',
        textColor: colors.export.excel.main,
        borderColor: colors.export.excel.main,
      },
      hover: {
        backgroundColor: colors.export.excel.hover,
        textColor: colors.export.excel.main,
        borderColor: colors.export.excel.main,
      },
      pressed: {
        backgroundColor: colors.export.excel.pressed,
        textColor: colors.export.excel.main,
        borderColor: colors.export.excel.main,
      },
      focus: {
        backgroundColor: 'transparent',
        textColor: colors.export.excel.main,
        borderColor: colors.export.excel.main,
      },
    },
  },
  {
    format: 'PDF',
    label: FILE_FORMAT_REGISTRY.PDF.label,
    customVariant: {
      default: {
        backgroundColor: 'transparent',
        textColor: colors.export.pdf.main,
        borderColor: colors.export.pdf.main,
      },
      hover: {
        backgroundColor: colors.export.pdf.hover,
        textColor: colors.export.pdf.main,
        borderColor: colors.export.pdf.main,
      },
      pressed: {
        backgroundColor: colors.export.pdf.pressed,
        textColor: colors.export.pdf.main,
        borderColor: colors.export.pdf.main,
      },
      focus: {
        backgroundColor: 'transparent',
        textColor: colors.export.pdf.main,
        borderColor: colors.export.pdf.main,
      },
    },
  },
  {
    format: 'WORD',
    label: FILE_FORMAT_REGISTRY.WORD.label,
    customVariant: {
      default: {
        backgroundColor: 'transparent',
        textColor: colors.export.word.main,
        borderColor: colors.export.word.main,
      },
      hover: {
        backgroundColor: colors.export.word.hover,
        textColor: colors.export.word.main,
        borderColor: colors.export.word.main,
      },
      pressed: {
        backgroundColor: colors.export.word.pressed,
        textColor: colors.export.word.main,
        borderColor: colors.export.word.main,
      },
      focus: {
        backgroundColor: 'transparent',
        textColor: colors.export.word.main,
        borderColor: colors.export.word.main,
      },
    },
  },
];

export const ExportDropdown: React.FC<ExportDropdownProps> = ({
  className = '',
  cardClassName = '',
  onExport,
  placement = 'right',
  closeOnOutsideClick = true,
  closeOnSelect = true,
  disabled = false,
  isLoading = false,
  availableFormats = ['EXCEL', 'PDF', 'WORD'],
  disabledFormats = [],
  disabledFormatMessageKey = 'common:messages.formatComingSoon',
}) => {
  const { t: translate } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const exportOptions = useMemo(
    () => DEFAULT_EXPORT_OPTIONS.filter((option) => availableFormats.includes(option.format)),
    [availableFormats],
  );

  const toggle = useCallback(() => {
    if (disabled || isLoading) return;
    setOpen(previous => !previous);
  }, [disabled, isLoading]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggleClick = useCallback(() => {
    toggle();
    // Remove focus from trigger button after click using setTimeout to ensure DOM is ready
    setTimeout(() => {
      if (triggerRef.current) {
        const button = triggerRef.current.querySelector('button');
        button?.blur();
      }
    }, 0);
  }, [toggle]);

  useEffect(() => {
    if (!open || !closeOnOutsideClick) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (cardRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closeOnOutsideClick, close]);

  const handleExportClick = useCallback((format: UIExportFormat) => {
    if (disabledFormats.includes(format)) return;
    onExport(format);
    if (closeOnSelect) {
      close();
    }
  }, [onExport, closeOnSelect, close, disabledFormats]);

  return (
    <div className={`relative inline-block ${className}`} ref={triggerRef}>
      <LinkButton
        iconLeft="import"
        onClick={handleToggleClick}
        disabled={disabled || isLoading}
        isLoading={isLoading}
        className="focus:outline-none focus:ring-0 [&:focus]:!shadow-none"
      >
        {translate('attendance:monthly.actions.export')}
      </LinkButton>

      {open && (
        <div
          ref={cardRef}
          className={`absolute mt-2 min-w-[200px] ${placement === 'right' ? 'right-0' : 'left-0'}`}
          style={{ zIndex: zIndex.dropdown }}
        >
          <Card className={`flex flex-col gap-2 ${cardClassName}`} padding="16px">
            <div className="flex flex-col gap-2">
              {exportOptions.map((option) => {
                const isOptionDisabled = disabledFormats.includes(option.format);
                return (
                  <PrimaryButton
                    key={option.format}
                    onClick={() => handleExportClick(option.format)}
                    variant="outline"
                    customVariant={option.customVariant}
                    fullWidth
                    disabled={isOptionDisabled}
                    className="!rounded-[12px]"
                    textVariant="body-medium"
                    data-testid={`export-option-${option.format.toLowerCase()}`}
                  >
                    {isOptionDisabled
                      ? translate(disabledFormatMessageKey, { format: option.label })
                      : option.label}
                  </PrimaryButton>
                );
              })}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ExportDropdown;
